import { Box, Skeleton, Text } from '@chakra-ui/react';
import React, { useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import { useMutation } from 'react-query';
import { userActivity } from '../../../../apis/V2/userActivity';
import { axiosErrorHandler } from '../../../../apis/axios';
import useTimer from '../../../../hooks/V2/useTimer';

interface LectureVideoProps {
	_id: string;
	title: string;
	videoUrl: string;
	instructorLine: string;
}

const LectureVideo: React.FC<LectureVideoProps> = ({ _id, title, videoUrl, instructorLine }) => {
	// const [timer, setTimer] = useState<number>(0);
	const playerRef = useRef<any>();
	const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
	const [isBuffering, setIsBuffering] = useState<boolean>(false);
	const [videoDuration, setVideoDuration] = useState<number | null>(null);
	const [maxTimeStamp, setMaxTimeStamp] = useState<number>(0);

	const { time: timer, isRunning, startTimer, stopTimer, resetTimer, changeSpeed, setTime: setTimer } = useTimer(0);

	const maxTimeStampRef = useRef(maxTimeStamp);
	const startRef = useRef(() => startTimer());
	const stopRef = useRef(() => stopTimer());

	const seekToTime = (timeInSeconds: number) => {
		if (playerRef.current) {
			playerRef.current.seekTo(timeInSeconds, 'seconds');
		}
	};

	const { mutate: userActivityMutate, isLoading: isUserActivityLoading } = useMutation(userActivity, {
		onSuccess: data => {
			if (data) {
				// Extract userWatchTime and lastTimeStamp from the response data
				const { userWatchTime, lastTimeStamp } = data.data.userProgress;

				// Check if they are greater than the current state values
				if (userWatchTime > timer) {
					setTimer(userWatchTime);
				}

				if (!isVideoPlaying) {
					seekToTime(lastTimeStamp);
				}

				if (lastTimeStamp > maxTimeStamp) {
					setMaxTimeStamp(lastTimeStamp);
				}
			}
		},
		onError: error => {
			const e = axiosErrorHandler(error);
			if (typeof e === 'object' && 'message' in e) {
				console.log(e.message);
			} else {
				console.log('Something went wrong');
			}
		},
	});

	const sendUserActivityData = () => {
		const data = {
			entityType: 'Lecture',
			entity: _id,
			userWatchTime: Math.round(timer),
			lastTimeStamp: Math.round(maxTimeStampRef.current),
			totalDuration: videoDuration,
		};
		userActivityMutate(data);
	};

	const handleMaxTimeStamp = (progress: any) => {
		const maxViewed = progress.playedSeconds;
		if (maxTimeStampRef.current < maxViewed) {
			maxTimeStampRef.current = maxViewed;
			setMaxTimeStamp(maxViewed);
		}
		console.log('timer:', Math.round(timer), +timer.toFixed(3));
		if (Math.round(timer) % 30 === 0) {
			sendUserActivityData();
		}
		console.log(`Max Time viewed: ${maxViewed}`);
	};

	const handlePlayBackRateChange = (playback: number) => {
		console.log('playback:', playback);
		changeSpeed(playback);
	};

	const handleVideoPlay = () => {
		setIsVideoPlaying(true);
		startRef.current();
	};

	const handleVideoPause = () => {
		setIsVideoPlaying(false);
		stopRef.current();
		sendUserActivityData();
	};

	const handleBuffer = () => {
		setIsBuffering(true);
		stopRef.current();
		sendUserActivityData();
	};

	const handleBufferEnd = () => {
		setIsBuffering(false);
		startRef.current();
	};

	const handleDuration = (duration: number) => {
		setVideoDuration(duration);
	};

	return (
		<Box>
			<Skeleton
				isLoaded={true}
				rounded="16px"
				sx={{
					'& iframe': {
						borderRadius: '16px',
						overflow: 'hidden',
					},
				}}
				border="1px solid #E7E6E6"
				bg="black"
				h="0"
				pb="56.25%"
				position="relative"
			>
				<ReactPlayer
					ref={playerRef}
					style={{ position: 'absolute', top: '0', left: '0' }}
					width="100%"
					height="100%"
					url={videoUrl}
					controls={true}
					onProgress={handleMaxTimeStamp}
					onStart={handleVideoPlay}
					onPlay={handleVideoPlay}
					onPause={handleVideoPause}
					onBuffer={handleBuffer}
					onBufferEnd={handleBufferEnd}
					onDuration={handleDuration}
					onPlaybackRateChange={handlePlayBackRateChange}
					onEnded={sendUserActivityData}
				/>
			</Skeleton>
			<Box mt="16px">
				<Text mt="16px" textStyle={{ base: 'h4', md: 'h2' }}>
					{title}
				</Text>
				<Text mt="8px" textStyle={{ base: 'caption', md: 'body1' }} color="#848081">
					{instructorLine}
				</Text>
			</Box>
		</Box>
	);
};

export default LectureVideo;
